import * as React from 'react'

import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion'
import { SMALL_ICON_BASE_SRC } from '../../utils/constants'

interface HintIndicatorProps {
  /**
   * Ref to the element whose visibility should be tracked. The indicator
   * shows whenever this element is partially or completely out of the
   * viewport, and on click smoothly scrolls it into view.
   */
  targetRef: React.RefObject<HTMLElement>;
}

const HORIZONTAL_OFFSET_FROM_TARGET = 16

const HintIndicator = ({ targetRef }: HintIndicatorProps) => {
  const [isOutOfView, setIsOutOfView] = React.useState(false)
  const [leftOffset, setLeftOffset] = React.useState<number | null>(null)
  const prefersReducedMotion = usePrefersReducedMotion()

  const updatePosition = React.useCallback(() => {
    const target = targetRef.current
    if (!target) {
      setIsOutOfView(false)
      setLeftOffset(null)
      return
    }
    const rect = target.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const fullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight
    setIsOutOfView(!fullyVisible)
    setLeftOffset(rect.right + HORIZONTAL_OFFSET_FROM_TARGET)
  }, [targetRef])

  React.useEffect(() => {
    updatePosition()

    window.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)

    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updatePosition)
      if (targetRef.current) {
        resizeObserver.observe(targetRef.current)
      }
      resizeObserver.observe(document.documentElement)
    }

    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [updatePosition, targetRef])

  const handleClick = () => {
    const target = targetRef.current
    if (!target) { return }
    const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth'
    target.scrollIntoView({ behavior, block: 'start' })
  }

  const style = leftOffset != null ? { left: `${leftOffset}px` } : undefined

  return (
    <button
      aria-hidden={!isOutOfView}
      className={`hint-indicator ${isOutOfView ? 'visible' : 'hidden'}`}
      onClick={handleClick}
      style={style}
      tabIndex={isOutOfView ? 0 : -1}
      type="button"
    >
      <span className="hint-indicator__icon-and-label">
        <img alt="" className="hint-indicator__lightbulb" src={`${SMALL_ICON_BASE_SRC}/lightbulb.svg`} />
        <span className="hint-indicator__label">Hint</span>
      </span>
      <img alt="" className="hint-indicator__chevron" src={`${SMALL_ICON_BASE_SRC}/chevron-wide-down.svg`} />
    </button>
  )
}

export { HintIndicator }
